/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from '@/assets/images/logo.png';
import heroAerial from '@/assets/images/hero-aerial.jpg';
import heroSunrise from '@/assets/images/hero-sunrise.jpg';
import runwayNight from '@/assets/images/runway-night.jpg';
import femaAircraft from '@/assets/images/fema-aircraft.jpg';
import newsletterImage from '@/assets/images/newsletter-image.jpg';

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings
  '/images/logo.png': logo,
  '/assets/images/logo.png': logo,
  '/media/1a717a_88c8c12cd19c4581adf6c1521991bbdc~mv2.png': logo,
  
  // Hero image mappings
  '/images/hero-aerial.jpg': heroAerial,
  '/assets/images/hero-aerial.jpg': heroAerial,
  '/media/1a717a_19349900af754697aea87e0338e8fbf7~mv2.jpg': heroAerial,
  
  '/images/hero-sunrise.jpg': heroSunrise,
  '/assets/images/hero-sunrise.jpg': heroSunrise,
  '/media/1a717a_646ef3b2b3a143eaadddda44a8f4a0b7~mv2.jpeg': heroSunrise,
  
  // Service section image mappings
  '/images/runway-night.jpg': runwayNight,
  '/assets/images/runway-night.jpg': runwayNight,
  '/media/Night_RWY-453x286.jpg': runwayNight,
  '/media/1a717a_31716fdaac174f9993c425b855ab3bd8~mv2.jpg': runwayNight,
  
  '/images/fema-aircraft.jpg': femaAircraft,
  '/assets/images/fema-aircraft.jpg': femaAircraft,
  '/media/FEMA-3-12-453x286.jpg': femaAircraft,
  '/media/1a717a_7b811f01840e4b1386559aa396e0249b~mv2.jpg': femaAircraft,
  
  '/images/newsletter-image.jpg': newsletterImage,
  '/assets/images/newsletter-image.jpg': newsletterImage,
  '/media/IMG_2707.jpeg': newsletterImage,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
